// externals
import type { FC } from 'react';

// libraries
import { FeeCategorySelectInput } from './FeeCategorySelectInput';
import { FeesFormItemsContainer } from './FeesForm.style';
import { FinanceAmountCell } from './FinanceAmountCell';
import { FinanceCodeCell } from './FinanceCodeCell';
import { FinanceDeleteCell } from './FinanceDeleteCell';
import { FinanceNameCell } from './FinanceNameCell';
import { LeaseUpfrontCell } from './LeaseUpfrontCell';
import { TaxedCell } from './TaxedCell';

// types
import type { Detail } from './types';

// utils
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DELETED_BY_ACTION_FEE_AMOUNT } from './constants';
import { getFeeCodes, isLeaseFinanceDetail } from './utils';
import { Box } from '@interstate/components/Box';
import { useBaseDealBreakpoint } from '../../utils/useBaseDealBreakpoint';

type FeeDetailRowProps = {
    detail: Detail;
    index: number;
    isLease?: boolean;
};

export const FeeDetailRow: FC<FeeDetailRowProps> = ({ detail, index, isLease }) => {
    const allItemizedFees = useSelector(offerSelectors.getCombinedItemizedFees);
    const appliedFeeCodes = getFeeCodes(allItemizedFees);
    const isInlineTaxedIndicatorEnabled = useSelector(featureToggleSelectors.isInlineTaxedIndicatorEnabled);

    const baseBreakPoint = useBaseDealBreakpoint();

    if (detail.amount === DELETED_BY_ACTION_FEE_AMOUNT && detail.isEdited === false) return null;

    const isNewFee = !appliedFeeCodes.includes(detail.code ?? -1);
    const isCodeValid = Boolean(detail.code ?? 0);

    const feesCategory =
        isNewFee || !isCodeValid ? (
            <FeeCategorySelectInput feeCategory={detail.category} feeIndex={index} detail={detail} />
        ) : (
            <FinanceCodeCell detail={detail} index={index} />
        );

    return (
        <FeesFormItemsContainer
            data-testid={`edit-fee-row-${detail.category}-${index}`}
            key={`edit-fee-row-${index}`}
            baseBreakPoint={baseBreakPoint}
            isManualFee={detail.isManual}
            isTaxedIndicatorEnabled={isInlineTaxedIndicatorEnabled}
        >
            {feesCategory}
            <FinanceNameCell detail={detail} index={index} feeCategory={detail.category} />
            <FinanceAmountCell detail={detail} index={index} />
            <Box data-testid="edit-fee-row-upfront-container" className="edit-fee---upfront-container">
                {isLeaseFinanceDetail(!!isLease, detail) && <LeaseUpfrontCell detail={detail} index={index} />}
                {isInlineTaxedIndicatorEnabled && <TaxedCell detail={detail} index={index} />}
                {<FinanceDeleteCell detail={detail} index={index} />}
            </Box>
        </FeesFormItemsContainer>
    );
};
